import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Printer } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrintWorkReport = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const workId = searchParams.get('workId');
  
  const [work, setWork] = useState(null);
  const [workDetails, setWorkDetails] = useState([]);
  const [debt, setDebt] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!workId) {
        setLoading(false);
        return;
      }
      
      try {
        // Load work
        const workRes = await fetch(`${API}/work`);
        if (workRes.ok) {
          const works = await workRes.json();
          const foundWork = works.find(w => w.id === workId);
          setWork(foundWork);
        }
        
        // Load work details
        const detailsRes = await fetch(`${API}/work-details/${workId}`);
        if (detailsRes.ok) {
          const details = await detailsRes.json();
          setWorkDetails(details);
          
          // Calculate debt (total customer price - paid amount)
          // For now, assume full amount is debt if not paid
          const totalCustomerPrice = details.reduce((sum, item) => {
            const calculatedPrice = ((parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0)) - ((parseFloat(item.quantity) || 0) * (parseFloat(item.cost) || 0));
            return sum + calculatedPrice;
          }, 0);
          
          // Check if there's a payment record for this work
          try {
            const paymentsRes = await fetch(`${API}/work-payments/${workId}`);
            if (paymentsRes.ok) {
              const payments = await paymentsRes.json();
              const paidAmount = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
              const remainingDebt = totalCustomerPrice - paidAmount;
              setDebt(remainingDebt > 0 ? remainingDebt : 0);
            } else {
              // No payments endpoint, show full amount as debt
              setDebt(totalCustomerPrice);
            }
          } catch (e) {
            // If no payments API, show full amount as debt
            setDebt(totalCustomerPrice);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [workId]);

  const handlePrint = () => {
    window.print();
  };

  const total = workDetails.reduce((sum, item) => {
    // מחיר ללקוח = מחיר ליחידה × כמות (סכום)
    const calculatedPrice = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
    return sum + calculatedPrice;
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" dir="rtl">
        <p className="text-xl">טוען...</p>
      </div>
    );
  }

  if (!work) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4" dir="rtl">
        <p className="text-xl text-red-600">לא נמצאה עבודה</p>
        <Button onClick={() => navigate('/reports')}>
          <ArrowRight className="ml-2 h-4 w-4" />
          חזרה לדיווחים
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white print-work-report" dir="rtl">
      {/* Print Controls - Hidden when printing */}
      <div className="print-hide bg-gray-100 p-4 flex justify-between items-center sticky top-0 z-10 shadow">
        <Button variant="outline" onClick={() => navigate('/reports')}>
          <ArrowRight className="ml-2 h-4 w-4" />
          חזרה
        </Button>
        <h2 className="text-lg font-bold">תצוגה מקדימה להדפסה</h2>
        <Button onClick={handlePrint} className="bg-orange-500 hover:bg-orange-600">
          <Printer className="ml-2 h-4 w-4" />
          הדפס עכשיו
        </Button>
      </div>

      {/* Printable Content */}
      <div className="p-10 max-w-4xl mx-auto">
        {/* Company Header */}
        <div className="text-center mb-8 border-b-2 border-orange-300 pb-6">
          <h1 className="text-3xl font-bold text-orange-600 mb-2">אימן שירותי מחשוב</h1>
          <p className="text-lg text-gray-700 mb-3">
            התקנת רשתות תקשורת | מכירה והתקנת מחשבים נייחים וניידים | מצלמות ומעגל סגור | אינטרקומים
          </p>
          <div className="flex justify-center gap-8 text-gray-600">
            <span>📧 aiman@aimanpc.com</span>
            <span>📞 052-576-7555</span>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-center text-orange-600 mb-2">
          דוח עבודה ללקוח
        </h2>
        <p className="text-center text-gray-500 mb-8">
          תאריך הדפסה: {new Date().toLocaleDateString('he-IL')}
        </p>

        {/* Client Info */}
        <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6 mb-8">
          <h3 className="text-xl font-bold text-orange-600 mb-4">פרטי לקוח</h3>
          <div className="grid grid-cols-2 gap-4">
            <p><span className="font-bold">שם העבודה:</span> {work.workName}</p>
            <p><span className="font-bold">שם מזמין:</span> {work.clientName}</p>
            {work.clientPhone && (
              <p><span className="font-bold">טלפון:</span> {work.clientPhone}</p>
            )}
            <p><span className="font-bold">תאריך:</span> {work.date}</p>
          </div>
        </div>

        {/* Table */}
        <table className="w-full border-collapse border-2 border-gray-400">
          <thead>
            <tr className="bg-orange-500 text-white">
              <th className="border-2 border-gray-400 p-3 text-center">#</th>
              <th className="border-2 border-gray-400 p-3 text-center">שם חלק</th>
              <th className="border-2 border-gray-400 p-3 text-center">כמות</th>
              <th className="border-2 border-gray-400 p-3 text-center">מחיר ליחידה</th>
              <th className="border-2 border-gray-400 p-3 text-center">מחיר ללקוח</th>
            </tr>
          </thead>
          <tbody>
            {workDetails.map((item, index) => {
              // מחיר ללקוח = מחיר ליחידה × כמות (סכום)
              const calculatedPrice = (parseFloat(item.unitPrice) || 0) * (parseFloat(item.quantity) || 0);
              return (
                <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-orange-50'}>
                  <td className="border-2 border-gray-400 p-3 text-center">{index + 1}</td>
                  <td className="border-2 border-gray-400 p-3 text-center">{item.partName}</td>
                  <td className="border-2 border-gray-400 p-3 text-center">{item.quantity || '-'}</td>
                  <td className="border-2 border-gray-400 p-3 text-center">
                    {item.unitPrice ? `₪${item.unitPrice}` : '-'}
                  </td>
                  <td className="border-2 border-gray-400 p-3 text-center font-bold text-orange-600">
                    ₪{calculatedPrice.toLocaleString()}
                  </td>
                </tr>
              );
            })}
            <tr className="bg-orange-200 font-bold">
              <td colSpan="4" className="border-2 border-gray-400 p-4 text-center text-lg">
                סה"כ מחיר ללקוח:
              </td>
              <td className="border-2 border-gray-400 p-4 text-center text-xl text-orange-600">
                ₪{total.toLocaleString()}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            margin: 1cm;
            size: A4;
          }
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          html, body {
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
          /* Hide toolbar, navigation, and non-print elements */
          .print-hide,
          .print\\:hidden,
          nav,
          header,
          [data-testid="emergent-badge"],
          iframe {
            display: none !important;
            visibility: hidden !important;
            height: 0 !important;
            overflow: hidden !important;
          }
          .min-h-screen {
            min-height: auto !important;
            height: auto !important;
          }
          .bg-white {
            background-color: white !important;
          }
          table {
            page-break-inside: auto;
          }
          tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
};

export default PrintWorkReport;
